// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { WishListCustomLineModel } from "@bg/App/Services/WishListService";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { GetWishListLinesApiParameter } from "@insite/client-framework/Services/WishListService";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import {
    WishListLineCollectionModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

export type ProductListDetailsState = {
    temporaryEmptyLists: string[];
    wishList: WishListModel | null;
    wishListLines: WishListLineModel[] | null;
    productLinesQtyToUpdate: WishListCustomLineModel[];
};

export type xProductListDetailsState = {
    xProductListDetails: ProductListDetailsState;
};

const initialState: ProductListDetailsState = {
    temporaryEmptyLists: [],
    wishList: null,
    wishListLines: null,
    productLinesQtyToUpdate: [],
};

const xProductListDetails = createTypedReducerWithImmer(initialState, {
    "Pages/xProductListDetails/TemporarilySaveEmptyList": (
        draft: Draft<ProductListDetailsState>,
        action: { emptyList: string },
    ) => {
        draft.temporaryEmptyLists.push(action.emptyList);
    },
    "Data/WishLists/CompleteLoadWishList": (
        draft: Draft<ProductListDetailsState>,
        actions: { wishList: WishListModel },
    ) => {
        if (actions.wishList) {
            draft.wishList = actions.wishList;
        }
    },
    "Data/WishLists/ClearWishList": (draft: Draft<ProductListDetailsState>) => {
        draft.wishList = null;
        draft.wishListLines = null;
    },
    "Data/WishListLines/CompleteLoadWishListLines": (
        draft: Draft<ProductListDetailsState>,
        actions: { parameter: GetWishListLinesApiParameter; result: WishListLineCollectionModel },
    ) => {
        if (actions.result.wishListLines?.length) {
            draft.wishListLines = actions.result.wishListLines;
        }
    },
    "Pages/xProductListDetails/UpdateProductsQty": (
        draft: Draft<ProductListDetailsState>,
        action: { lineModel: WishListCustomLineModel & { originalQty?: number } },
    ) => {
        const existingObjectIndex = draft.productLinesQtyToUpdate.findIndex(
            item =>
                item.wishListLineId === action.lineModel.wishListLineId &&
                item.groupLineId === action.lineModel.groupLineId,
        );

        if (existingObjectIndex !== -1) {
            if (action.lineModel.qty === action.lineModel.originalQty) {
                draft.productLinesQtyToUpdate = draft.productLinesQtyToUpdate.filter(
                    (element, index) => index !== existingObjectIndex,
                );
            } else {
                delete action.lineModel.originalQty;
                draft.productLinesQtyToUpdate[existingObjectIndex] = {
                    ...action.lineModel,
                };
            }
        } else {
            delete action.lineModel.originalQty;
            draft.productLinesQtyToUpdate.push(action.lineModel);
        }
    },
    "Pages/xProductListDetails/ClearProductsQty": (draft: Draft<ProductListDetailsState>) => {
        draft.productLinesQtyToUpdate = [];
    },
});

(reducers as any).xProductListDetails = xProductListDetails;

type xProductListDetailsReducer = {
    productListDetails: typeof xProductListDetails;
};

export type xProductListDetailsActions =
    | Parameters<xProductListDetailsReducer[keyof xProductListDetailsReducer]>[1]
    | AnyAction;
